// ── PoemMemoryPanel ───────────────────────────────────────────────────────────
// Manuscript Layers layout — one outer card, three internal zones:
//
//   ┌──────────────────────────────────────┐
//   │░ POEM OVERVIEW  [gold band]          │  global — once per poem
//   ╠══════════════════════════════════════╣  global/local boundary
//   │  VERSE ANALYSIS [content body]       │  local — per bayt, navigable
//   │ · · · · · · · · · · · · · · · · · · │  dotted hairline
//   │  NOTES          [marginalia]         │  optional — collapsed by default
//   └──────────────────────────────────────┘
//   [paste-back panel]  ← detached, below card, only when pendingPasteType set
//
// State ownership: session is passed in from AskAiSection (the coordinator).
// This component writes via poemSessionManager and notifies upward via callbacks.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { poemSessionManager } from '../lib/poem-session-manager.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

function isArabicText(str) {
  if (!str) return false;
  const arabicChars = (str.match(/[؀-ۿ]/g) || []).length;
  return arabicChars / str.length > 0.3;
}

function getBaytNum(verseId, displayVerses) {
  if (!verseId || !displayVerses?.length) return null;
  let idx = displayVerses.findIndex(v => v.id === verseId);
  if (idx < 0) {
    const m = verseId.match(/_v(\d+)$/);
    if (m) idx = parseInt(m[1], 10);
  }
  return idx >= 0 ? idx + 1 : null;
}

// ── Design tokens (manuscript layers) ────────────────────────────────────────

const GOLD_BG     = 'rgba(212,168,83,0.06)';
const GOLD_BORDER = 'rgba(212,168,83,0.35)';
const GOLD_LABEL  = 'rgba(212,168,83,0.75)';

// ── Small shared UI ───────────────────────────────────────────────────────────

function MicroLink({ onClick, children, style }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none', border: 'none', padding: 0,
        fontSize: '0.58rem', color: 'var(--text3)',
        cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif",
        letterSpacing: '0.06em', textDecoration: 'underline',
        textUnderlineOffset: '2px', flexShrink: 0,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// ── PasteBackPanel — detached, appears below the main card ───────────────────

function PasteBackPanel({ type, existingSummary, onSave, onCancel, saving, savedFlash }) {
  const [pasteText,  setPasteText]  = useState('');
  const [mode,       setMode]       = useState(
    type === 'verse' && existingSummary ? 'append' : 'replace'
  );
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) textareaRef.current.focus();
  }, []);

  const isOverview  = type === 'overview';
  const hasExisting = Boolean(existingSummary);
  const summaryIsAr = isArabicText(pasteText);

  return (
    <div style={{
      marginTop: '0.5rem',
      background: 'var(--bg3)', border: '1px solid var(--border)',
      borderRadius: 4, padding: '0.85rem 1rem',
      display: 'flex', flexDirection: 'column', gap: '0.6rem',
    }}>
      {/* Label */}
      <div style={{
        fontFamily: "'Josefin Sans', sans-serif",
        fontSize: '0.52rem', letterSpacing: '0.15em',
        textTransform: 'uppercase',
        color: isOverview ? GOLD_LABEL : 'var(--text3)',
      }}>
        {isOverview ? 'Paste Poem Overview' : 'Paste Verse Analysis'}
      </div>

      <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--text3)', lineHeight: 1.55 }}>
        Copy the block labelled{' '}
        <strong style={{ color: 'var(--text2)' }}>
          {isOverview ? '"Poem Overview to Paste Back:"' : '"Summary to Paste Back:"'}
        </strong>{' '}
        from the AI's response and paste it below.
      </p>

      {/* Replace / Append toggle — verse only, when existing summary present */}
      {!isOverview && hasExisting && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span style={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: '0.54rem', letterSpacing: '0.08em',
            color: 'var(--text3)', textTransform: 'uppercase',
            marginRight: '0.2rem',
          }}>
            Save as:
          </span>
          {[
            { id: 'append',  label: 'Append' },
            { id: 'replace', label: 'Replace' },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setMode(opt.id)}
              style={{
                padding: '0.25rem 0.6rem', borderRadius: 3,
                border: '1px solid',
                borderColor: mode === opt.id ? 'var(--em)' : 'var(--border)',
                background:  mode === opt.id ? 'var(--em-glow)' : 'transparent',
                color:       mode === opt.id ? 'var(--em)' : 'var(--text3)',
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.6rem', letterSpacing: '0.08em',
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          ))}
          {mode === 'append' && (
            <span style={{
              fontSize: '0.58rem', color: 'var(--text3)',
              fontFamily: "'Josefin Sans', sans-serif",
              lineHeight: 1.4,
            }}>
              — adds below existing memory
            </span>
          )}
        </div>
      )}

      <textarea
        ref={textareaRef}
        value={pasteText}
        onChange={e => setPasteText(e.target.value)}
        placeholder={isOverview
          ? "Paste the AI's poem overview here…"
          : "Paste the AI's summary here…"}
        rows={isOverview ? 4 : 6}
        style={{
          width: '100%', boxSizing: 'border-box',
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 4, padding: '0.65rem 0.75rem',
          color: 'var(--text)', fontSize: '0.82rem',
          lineHeight: 1.65, resize: 'vertical',
          fontFamily: "'Georgia', serif", outline: 'none',
          direction: summaryIsAr ? 'rtl' : 'ltr',
        }}
      />

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          onClick={() => pasteText.trim() && onSave(pasteText.trim(), mode)}
          disabled={!pasteText.trim() || saving}
          style={{
            flex: 1, padding: '0.65rem',
            background:  pasteText.trim() ? 'var(--em-glow)' : 'var(--bg3)',
            border: '1px solid',
            borderColor: pasteText.trim() ? 'var(--em)' : 'var(--border2)',
            borderRadius: 4,
            color: pasteText.trim() ? 'var(--em)' : 'var(--text3)',
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: '0.62rem', letterSpacing: '0.1em',
            textTransform: 'uppercase',
            cursor: pasteText.trim() ? 'pointer' : 'default',
            transition: 'background 0.1s, border-color 0.1s',
          }}
        >
          {saving ? 'Saving…' : isOverview ? 'Save poem overview' : 'Save to poem memory'}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '0.65rem 0.85rem',
            background: 'none', border: '1px solid var(--border2)',
            borderRadius: 4, color: 'var(--text3)',
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: '0.62rem', cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>

      {savedFlash && (
        <div style={{
          fontFamily: "'Josefin Sans', sans-serif",
          fontSize: '0.6rem', letterSpacing: '0.08em',
          color: '#34d399', lineHeight: 1.4,
        }}>
          {isOverview
            ? '✓ Poem overview saved'
            : '✓ Saved — next verse prompt will include this context'}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * @param {{
 *   poemId:             string,
 *   currentVerseId:     string,
 *   pendingPasteType:   'verse' | 'overview' | null,
 *   session:            import('../lib/poem-session-types.js').PoemSession | null,
 *   displayVerses?:     object[],
 *   onSessionChange:    (updated) => void,
 *   onSaved:            () => void,
 *   onNavigateToVerse?: (verseId: string) => void,
 *   onRequestPaste?:    (type: 'verse' | 'overview') => void,
 * }} props
 */
export default function PoemMemoryPanel({
  poemId,
  currentVerseId,
  pendingPasteType,
  session,
  displayVerses = [],
  onSessionChange,
  onSaved,
  onNavigateToVerse,
  onRequestPaste,
}) {
  // ── Save state ────────────────────────────────────────────────────────────────
  const [saving,         setSaving]         = useState(false);
  const [savedFlashType, setSavedFlashType] = useState(null); // 'overview' | 'verse' | null
  const [clearedFlash,   setClearedFlash]   = useState(null); // 'overview' | 'history' | null

  function flashSaved(type) {
    setSavedFlashType(type);
    setTimeout(() => setSavedFlashType(null), 2500);
  }

  function flashCleared(type) {
    setClearedFlash(type);
    setTimeout(() => setClearedFlash(null), 2000);
  }

  // ── Overview show-more ────────────────────────────────────────────────────────
  const [overviewExpanded, setOverviewExpanded] = useState(false);
  // Collapse when a new overview is saved
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setOverviewExpanded(false); }, [session?.poemOverview]);
  const OVERVIEW_PREVIEW_LINES = 4; // approx line threshold before "show more" appears

  // ── Verse history browsing ────────────────────────────────────────────────────
  const [browsedId, setBrowsedId] = useState(null);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setBrowsedId(null); }, [currentVerseId]);

  const history      = useMemo(() => session?.verseHistory ?? [], [session]);
  const activeId     = browsedId ?? currentVerseId;
  const activeEntry  = history.find(e => e.verseId === activeId) ?? null;
  const activeIdx    = history.findIndex(e => e.verseId === activeId);
  const isCurrentVerse = activeId === currentVerseId;

  const baytNum        = getBaytNum(activeId, displayVerses);
  const currentBaytNum = getBaytNum(currentVerseId, displayVerses);
  const totalBayts     = displayVerses.length || null;

  function goPrev() {
    if (!history.length) return;
    setBrowsedId(activeIdx <= 0
      ? history[history.length - 1].verseId
      : history[activeIdx - 1].verseId);
  }
  function goNext() {
    if (!history.length) return;
    setBrowsedId((activeIdx < 0 || activeIdx >= history.length - 1)
      ? history[0].verseId
      : history[activeIdx + 1].verseId);
  }
  function goToCurrent() { setBrowsedId(null); }

  // ── Notes editing ─────────────────────────────────────────────────────────────
  const [notesOpen,       setNotesOpen]       = useState(false);
  const [notesText,       setNotesText]       = useState('');
  const [savingNotes,     setSavingNotes]     = useState(false);
  const [notesSavedFlash, setNotesSavedFlash] = useState(false);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { if (!notesOpen) setNotesText(session?.additionalNotes ?? ''); }, [notesOpen, session?.additionalNotes]);

  // ── Paste-back handlers ───────────────────────────────────────────────────────

  const handleOverviewSave = useCallback(async (text) => {
    if (!poemId) return;
    setSaving(true);
    const updated = await poemSessionManager.updatePoemOverview(poemId, text);
    onSessionChange(updated);
    setSaving(false);
    flashSaved('overview');
    onSaved();
  }, [poemId, onSessionChange, onSaved]);

  const handleVerseSave = useCallback(async (text, mode) => {
    if (!poemId || !currentVerseId) return;
    setSaving(true);
    const finalText = mode === 'append' && session?.runningSummary
      ? `${session.runningSummary}\n\n---\n\n${text}`
      : text;
    const updated = await poemSessionManager.updateRunningSummary(poemId, finalText, currentVerseId);
    onSessionChange(updated);
    setSaving(false);
    flashSaved('verse');
    onSaved();
  }, [poemId, currentVerseId, session?.runningSummary, onSessionChange, onSaved]);

  const handleSaveNotes = useCallback(async () => {
    if (!poemId) return;
    setSavingNotes(true);
    const updated = await poemSessionManager.updateAdditionalNotes(poemId, notesText.trim());
    onSessionChange(updated);
    setSavingNotes(false);
    setNotesOpen(false);
    setNotesSavedFlash(true);
    setTimeout(() => setNotesSavedFlash(false), 2500);
  }, [poemId, notesText, onSessionChange]);

  const handleClearOverview = useCallback(async () => {
    if (!poemId) return;
    const updated = await poemSessionManager.clearPoemOverview(poemId);
    onSessionChange(updated);
    flashCleared('overview');
  }, [poemId, onSessionChange]);

  const handleClearVerseHistory = useCallback(async () => {
    if (!poemId) return;
    const updated = await poemSessionManager.clearVerseHistory(poemId);
    onSessionChange(updated);
    setBrowsedId(null);
    flashCleared('history');
  }, [poemId, onSessionChange]);

  const handleClearAll = useCallback(async () => {
    if (!poemId) return;
    await poemSessionManager.clearSession(poemId);
    onSessionChange(null);
    setBrowsedId(null);
    setNotesOpen(false);
  }, [poemId, onSessionChange]);

  // ── Text direction helpers ────────────────────────────────────────────────────
  const overviewIsAr = isArabicText(session?.poemOverview ?? '');
  const summaryIsAr  = isArabicText(activeEntry?.summarySnapshot ?? session?.runningSummary ?? '');
  const notesIsAr    = isArabicText(session?.additionalNotes ?? notesText);

  // Decide whether the overview is long enough to warrant a "show more" toggle.
  // Rough heuristic: >200 chars or >4 newlines.
  const overviewText    = session?.poemOverview ?? '';
  const overviewIsLong  = overviewText.length > 200 || (overviewText.match(/\n/g) ?? []).length > OVERVIEW_PREVIEW_LINES;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ marginBottom: '0.75rem' }}>

      {/* Section label sits above the card */}
      <div style={{
        fontFamily: "'Josefin Sans', sans-serif",
        fontSize: '0.58rem', letterSpacing: '0.15em',
        textTransform: 'uppercase', color: 'var(--text3)',
        marginBottom: '0.5rem',
      }}>
        Poem Memory
      </div>

      {/* ── ONE OUTER CARD ───────────────────────────────────────────────────── */}
      <div style={{
        border: '1px solid var(--border2)',
        borderRadius: 4,
        overflow: 'hidden',
      }}>

        {/* ══════════════════════════════════════════════════════════════════════
            ZONE 1 — POEM OVERVIEW (gold band — global content)
        ═══════════════════════════════════════════════════════════════════════ */}
        <div style={{
          background:  GOLD_BG,
          borderLeft:  `2px solid ${GOLD_BORDER}`,
          padding:     '0.7rem 0.85rem',
        }}>
          {/* Zone header row */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: session?.poemOverview ? '0.45rem' : 0,
          }}>
            <div style={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '0.52rem', letterSpacing: '0.16em',
              textTransform: 'uppercase', color: GOLD_LABEL,
            }}>
              Poem Overview
            </div>
            {session?.poemOverview && (
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {savedFlashType === 'overview' && (
                  <span style={{
                    fontFamily: "'Josefin Sans', sans-serif",
                    fontSize: '0.58rem', letterSpacing: '0.08em', color: '#34d399',
                  }}>✓ Updated</span>
                )}
                <MicroLink onClick={() => onRequestPaste?.('overview')} style={{ color: GOLD_LABEL }}>
                  update
                </MicroLink>
                <MicroLink onClick={handleClearOverview} style={{ color: GOLD_LABEL }}>
                  clear
                </MicroLink>
              </div>
            )}
          </div>

          {session?.poemOverview ? (
            <>
              <div style={{
                maxHeight: overviewExpanded ? 'none' : '7rem',
                overflow: overviewExpanded ? 'visible' : 'hidden',
                position: 'relative',
              }}>
                <p style={{
                  margin: 0, fontSize: '0.82rem',
                  color: 'var(--text2)', lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  direction: overviewIsAr ? 'rtl' : 'ltr',
                  textAlign:  overviewIsAr ? 'right' : 'left',
                }}>
                  {session.poemOverview}
                </p>
                {/* Fade gradient when collapsed and long */}
                {!overviewExpanded && overviewIsLong && (
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: '2rem',
                    background: 'linear-gradient(transparent, var(--bg2))',
                    pointerEvents: 'none',
                  }} />
                )}
              </div>
              {overviewIsLong && (
                <button
                  onClick={() => setOverviewExpanded(v => !v)}
                  style={{
                    marginTop: '0.35rem', background: 'none', border: 'none', padding: 0,
                    fontSize: '0.58rem', color: GOLD_LABEL,
                    cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif",
                    letterSpacing: '0.08em', textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                  }}
                >
                  {overviewExpanded ? 'Show less ↑' : 'Show full overview ↓'}
                </button>
              )}
            </>
          ) : (
            <>
              {clearedFlash === 'overview' && (
                <span style={{
                  display: 'block', marginBottom: '0.35rem',
                  fontFamily: "'Josefin Sans', sans-serif",
                  fontSize: '0.58rem', letterSpacing: '0.08em', color: '#34d399',
                }}>
                  ✓ Overview cleared
                </span>
              )}
              <p style={{
                margin: 0, fontSize: '0.78rem',
                color: GOLD_LABEL, opacity: 0.6,
                fontStyle: 'italic', lineHeight: 1.6,
              }}>
                No overview yet — generate one using "Poem Overview" in the Ask AI card below.
              </p>
            </>
          )}
        </div>

        {/* ── Full-width rule — GLOBAL / LOCAL boundary ── */}
        <div style={{ height: 1, background: 'var(--border)' }} />

        {/* ══════════════════════════════════════════════════════════════════════
            ZONE 2 — VERSE ANALYSIS (content body — local, per bayt)
        ═══════════════════════════════════════════════════════════════════════ */}
        <div style={{
          background: 'var(--bg3)',
          padding: '0.85rem 1rem',
        }}>
          {/* Bayt navigator — only shown when history exists */}
          {history.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '0.65rem',
            }}>
              <button
                onClick={goPrev}
                style={{
                  background: 'none', border: '1px solid var(--border2)',
                  borderRadius: 4, padding: '0.2rem 0.5rem',
                  color: 'var(--text3)', cursor: 'pointer',
                  fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.75rem',
                }}
              >‹</button>

              <div style={{
                fontSize: '0.6rem', letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: baytNum ? 'var(--text2)' : 'var(--text3)',
                fontFamily: "'Josefin Sans', sans-serif",
                textAlign: 'center',
              }}>
                {baytNum
                  ? `Bayt ${baytNum}${totalBayts ? ` of ${totalBayts}` : ''}`
                  : isCurrentVerse
                    ? `Current bayt${currentBaytNum ? ` (${currentBaytNum})` : ''}`
                    : 'Bayt memory'}
                {!isCurrentVerse && (
                  <button
                    onClick={goToCurrent}
                    style={{
                      marginLeft: '0.5rem', background: 'none', border: 'none',
                      padding: 0, fontSize: '0.54rem', color: 'var(--text3)',
                      cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif",
                      letterSpacing: '0.06em', textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                    }}
                  >
                    ← back to current
                  </button>
                )}
              </div>

              <button
                onClick={goNext}
                style={{
                  background: 'none', border: '1px solid var(--border2)',
                  borderRadius: 4, padding: '0.2rem 0.5rem',
                  color: 'var(--text3)', cursor: 'pointer',
                  fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.75rem',
                }}
              >›</button>
            </div>
          )}

          {/* Verse content */}
          {activeEntry ? (
            <>
              <p style={{
                margin: 0, fontSize: '0.82rem',
                color: 'var(--text2)', lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
                direction: summaryIsAr ? 'rtl' : 'ltr',
                textAlign:  summaryIsAr ? 'right' : 'left',
              }}>
                {activeEntry.summarySnapshot}
              </p>

              {!isCurrentVerse && onNavigateToVerse && (
                <button
                  onClick={() => onNavigateToVerse(activeId)}
                  style={{
                    marginTop: '0.75rem', background: 'none',
                    border: '1px solid var(--border2)',
                    borderRadius: 4, padding: '0.45rem 0.75rem',
                    fontSize: '0.6rem', letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: 'var(--text3)',
                    fontFamily: "'Josefin Sans', sans-serif",
                    cursor: 'pointer', display: 'block', width: '100%',
                  }}
                >
                  {baytNum ? `Go to Bayt ${baytNum} →` : 'Go to this bayt →'}
                </button>
              )}

              {isCurrentVerse && (
                <div style={{
                  marginTop: '0.6rem',
                  display: 'flex', alignItems: 'center', gap: '0.65rem',
                }}>
                  {savedFlashType === 'verse' ? (
                    <span style={{
                      fontSize: '0.58rem', color: 'var(--em)',
                      fontFamily: "'Josefin Sans', sans-serif",
                      letterSpacing: '0.08em',
                    }}>
                      ✓ Memory updated
                    </span>
                  ) : (
                    <MicroLink onClick={() => onRequestPaste?.('verse')}>
                      update
                    </MicroLink>
                  )}
                  <MicroLink onClick={handleClearVerseHistory}>
                    clear verse memory
                  </MicroLink>
                  {!session?.poemOverview && (
                    <MicroLink onClick={handleClearAll} style={{ marginLeft: 'auto' }}>
                      clear all
                    </MicroLink>
                  )}
                </div>
              )}
            </>
          ) : (
            <div>
              <p style={{
                margin: 0, fontSize: '0.8rem', color: 'var(--text3)',
                fontStyle: 'italic', lineHeight: 1.65,
              }}>
                {history.length === 0
                  ? 'No verse summaries yet. Ask AI about this verse and paste the response back.'
                  : isCurrentVerse
                    ? 'No summary for this bayt yet — ask AI and paste back to save one.'
                    : 'No summary saved for this bayt.'}
              </p>
              {/* P3b: Shortcut to open paste panel when current verse has no summary yet */}
              {isCurrentVerse && onRequestPaste && (
                <button
                  onClick={() => onRequestPaste('verse')}
                  style={{
                    marginTop: '0.6rem', background: 'none',
                    border: '1px solid var(--border2)',
                    borderRadius: 4, padding: '0.45rem 0.75rem',
                    fontSize: '0.6rem', letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: 'var(--text3)',
                    fontFamily: "'Josefin Sans', sans-serif",
                    cursor: 'pointer', display: 'block', width: '100%',
                  }}
                >
                  Paste AI response →
                </button>
              )}
              {!isCurrentVerse && onNavigateToVerse && (
                <button
                  onClick={() => onNavigateToVerse(activeId)}
                  style={{
                    marginTop: '0.65rem', background: 'none',
                    border: '1px solid var(--border2)',
                    borderRadius: 4, padding: '0.45rem 0.75rem',
                    fontSize: '0.6rem', letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: 'var(--text3)',
                    fontFamily: "'Josefin Sans', sans-serif",
                    cursor: 'pointer', display: 'block', width: '100%',
                  }}
                >
                  {baytNum ? `Go to Bayt ${baytNum} →` : 'Go to this bayt →'}
                </button>
              )}
              {history.length > 0 && !session?.poemOverview && isCurrentVerse && (
                <div style={{ marginTop: '0.5rem' }}>
                  <MicroLink onClick={handleClearAll}>clear all memory</MicroLink>
                </div>
              )}
            </div>
          )}

          {/* Cleared flash — lives outside the activeEntry branch so it survives re-render */}
          {clearedFlash === 'history' && (
            <div style={{
              marginTop: '0.5rem',
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '0.58rem', letterSpacing: '0.08em', color: '#34d399',
            }}>
              ✓ Verse memory cleared
            </div>
          )}

          {/* ── Dotted hairline — VERSE / NOTES boundary (marginalia) ── */}
          <div style={{
            margin: '0.75rem 0 0.55rem',
            borderTop: '1px dotted var(--border2)',
          }} />

          {/* ══════════════════════════════════════════════════════════════════
              ZONE 3 — ADDITIONAL NOTES (marginalia — optional, collapsed)
          ═════════════════════════════════════════════════════════════════ */}
          {notesOpen ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.52rem', letterSpacing: '0.14em',
                textTransform: 'uppercase', color: 'var(--text3)',
              }}>
                Notes
              </div>
              <textarea
                value={notesText}
                onChange={e => setNotesText(e.target.value)}
                placeholder="Your own observations — themes, connections, questions…"
                rows={4}
                autoFocus
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: 'var(--bg2)', border: '1px solid var(--border)',
                  borderRadius: 4, padding: '0.6rem 0.7rem',
                  color: 'var(--text)', fontSize: '0.8rem',
                  lineHeight: 1.6, resize: 'vertical',
                  fontFamily: "'Georgia', serif", outline: 'none',
                  direction: notesIsAr ? 'rtl' : 'ltr',
                }}
              />
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  style={{
                    flex: 1, padding: '0.5rem',
                    background: 'var(--em-glow)', border: '1px solid var(--em)',
                    borderRadius: 4, color: 'var(--em)',
                    fontFamily: "'Josefin Sans', sans-serif",
                    fontSize: '0.6rem', letterSpacing: '0.1em',
                    textTransform: 'uppercase', cursor: 'pointer',
                  }}
                >
                  {savingNotes ? 'Saving…' : 'Save notes'}
                </button>
                <button
                  onClick={() => setNotesOpen(false)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: 'none', border: '1px solid var(--border2)',
                    borderRadius: 4, color: 'var(--text3)',
                    fontFamily: "'Josefin Sans', sans-serif",
                    fontSize: '0.6rem', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* Collapsed marginalia row */
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              minHeight: '1.4rem',
            }}>
              {session?.additionalNotes ? (
                <>
                  <p style={{
                    margin: 0, flex: 1,
                    fontSize: '0.76rem', color: 'var(--text3)',
                    lineHeight: 1.55, whiteSpace: 'pre-wrap',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    direction: notesIsAr ? 'rtl' : 'ltr',
                    textAlign:  notesIsAr ? 'right' : 'left',
                  }}>
                    {session.additionalNotes}
                  </p>
                  <MicroLink
                    onClick={() => { setNotesText(session.additionalNotes); setNotesOpen(true); }}
                    style={{ marginLeft: '0.75rem' }}
                  >
                    edit
                  </MicroLink>
                </>
              ) : (
                <>
                  <span style={{
                    fontSize: '0.72rem', color: 'var(--text3)',
                    fontFamily: "'Josefin Sans', sans-serif",
                    fontStyle: 'italic', opacity: 0.65,
                  }}>
                    Notes
                  </span>
                  <MicroLink onClick={() => { setNotesText(''); setNotesOpen(true); }}>
                    + Add
                  </MicroLink>
                </>
              )}
              {notesSavedFlash && (
                <span style={{
                  marginLeft: '0.5rem', flexShrink: 0,
                  fontSize: '0.58rem', color: '#34d399',
                  fontFamily: "'Josefin Sans', sans-serif",
                  letterSpacing: '0.08em',
                }}>
                  ✓ Saved
                </span>
              )}
            </div>
          )}

          {/* Clear all — only shown when both overview and verse history exist */}
          {session?.poemOverview && (history.length > 0 || session?.runningSummary) && (
            <div style={{ marginTop: '0.6rem', textAlign: 'right' }}>
              <MicroLink onClick={handleClearAll}>clear all memory</MicroLink>
            </div>
          )}
        </div>
        {/* ── end outer card ── */}
      </div>

      {/* ── PASTE-BACK PANELS — detached, below the card ───────────────────── */}
      {pendingPasteType === 'overview' && (
        <PasteBackPanel
          type="overview"
          existingSummary={null}
          onSave={handleOverviewSave}
          onCancel={onSaved}
          saving={saving}
          savedFlash={savedFlashType === 'overview'}
        />
      )}
      {pendingPasteType === 'verse' && (
        <PasteBackPanel
          type="verse"
          existingSummary={session?.runningSummary ?? ''}
          onSave={handleVerseSave}
          onCancel={onSaved}
          saving={saving}
          savedFlash={savedFlashType === 'verse'}
        />
      )}

    </div>
  );
}
